import React from 'react';
import { DatePickerProps } from '@/shared/types/datepicker';
import { useDatePicker, useDateRange } from './hooks';
import { DateInput } from './molecules';
import { DatePickerPortal } from './organisms';
import { cn } from '@/shared/utils/cn';

const DatePicker: React.FC<DatePickerProps> = (props) => {
  const {
    mode = 'single',
    selectedDate: externalSelectedDate = null,
    selectedRange: externalSelectedRange = { start: null, end: null },
    onDateChange,
    onRangeChange,
    minDate,
    maxDate,
    locale = 'es-ES',
    weekDays = ['Dom', 'Lun', 'Mar', 'Mie', 'Jue', 'Vie', 'Sab'],
    monthNames = [
      'Enero',
      'Febrero',
      'Marzo',
      'Abril',
      'Mayo',
      'Junio',
      'Julio',
      'Agosto',
      'Septiembre',
      'Octubre',
      'Noviembre',
      'Diciembre',
    ],
    label = '',
    startOfWeek = 1,
    className = '',
    inputClassName = '',
    showWeekNumbers = false,
    yearRange = 12,
    closeOnSelect = false,
    autoCloseRange = true,
    placeholder = 'Seleccionar fecha',
    format = 'dd/MM/yyyy',
    calendarsCount = 1,
    disabled = false,
    renderHeader,
    renderDay,
    size = 'sm',
  } = props;

  const {
    state,
    selectedDate,
    selectedRange,
    selectedDateForInput,
    selectedRangeForInput,
    inputRef,
    isPortalReady,
    actions,
  } = useDatePicker(props);

  // Hook para manejo de rangos
  const { handleRangeSelection } = useDateRange(
    selectedRange,
    actions.setSelectedRange,
    minDate,
    maxDate,
    autoCloseRange
  );

  // Formatear fecha para mostrar en el input
  const formatDateForInput = (date: Date | null) => {
    // Validar que date sea un objeto Date válido
    if (!date || !(date instanceof Date) || isNaN(date.getTime())) {
      return '';
    }

    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();

    return format.replace('dd', day).replace('MM', month).replace('yyyy', year.toString());
  };

  const formatRangeForInput = (range: { start: Date | null; end: Date | null }) => {
    if (!range || (!range.start && !range.end)) return '';

    // Validar que start y end sean objetos Date válidos
    const startIsValid =
      range.start && range.start instanceof Date && !isNaN(range.start.getTime());
    const endIsValid = range.end && range.end instanceof Date && !isNaN(range.end.getTime());

    if (startIsValid && endIsValid) {
      return `${formatDateForInput(range.start)} - ${formatDateForInput(range.end)}`;
    }

    if (startIsValid) {
      return formatDateForInput(range.start);
    }

    if (endIsValid) {
      return formatDateForInput(range.end);
    }

    return '';
  };

  const handleDateSelect = (date: Date) => {
    // Validar que date sea un objeto Date válido
    if (!date || !(date instanceof Date) || isNaN(date.getTime())) {
      if (process.env.NODE_ENV !== 'production') {
        // eslint-disable-next-line no-console
        console.warn('DatePicker: Se recibió una fecha inválida:', date);
      }
      return;
    }

    if (mode === 'single') {
      actions.setSelectedDate(date);
    } else {
      // Usar la lógica del hook useDateRange para manejo de rangos
      handleRangeSelection(date, state.hoverDate);
    }
  };

  const handleDateHover = (date: Date | null) => {
    // Validar que date sea null o un objeto Date válido
    if (date !== null && (!(date instanceof Date) || isNaN(date.getTime()))) {
      if (process.env.NODE_ENV !== 'production') {
        // eslint-disable-next-line no-console
        console.warn('DatePicker: Se recibió una fecha inválida en hover:', date);
      }
      return;
    }

    actions.setHoverDate(date);
  };

  const handleAccept = () => {
    // Cuando se presiona Aceptar, confirmar la fecha/rango pendiente y activar onChange
    if (mode === 'single') {
      // Si closeOnSelect es false, confirmar la fecha pendiente
      if (!closeOnSelect && state.pendingDate !== null) {
        // Confirmar la fecha pendiente (actualizar el estado confirmado y llamar a onDateChange)
        // setSelectedDate con confirm=true actualiza el estado y llama a onDateChange
        actions.setSelectedDate(state.pendingDate, true);
        // También llamar a onDateChange explícitamente para asegurar que funcione en modo controlado
        onDateChange?.(state.pendingDate);
      }
    } else if (mode === 'range') {
      // Si autoCloseRange es false, confirmar el rango pendiente
      if (!autoCloseRange && (state.pendingRange.start || state.pendingRange.end)) {
        // Confirmar el rango pendiente (actualizar el estado confirmado y llamar a onRangeChange)
        // setSelectedRange con confirm=true actualiza el estado y llama a onRangeChange
        actions.setSelectedRange(state.pendingRange, true);
        // También llamar a onRangeChange explícitamente para asegurar que funcione en modo controlado
        onRangeChange?.(state.pendingRange);
      }
    }
    // Cerrar el picker después de confirmar
    actions.closePicker();
  };

  const handleCancel = () => {
    // Cuando se presiona Cancelar, descartar la fecha/rango pendiente y cerrar sin aplicar cambios
    if (mode === 'single' && !closeOnSelect) {
      // Limpiar la fecha pendiente
      actions.setPendingDate(null);
      // Restaurar la fecha original si es necesario
      actions.restoreOriginalDate();
    } else if (mode === 'range' && !autoCloseRange) {
      // Limpiar el rango pendiente
      actions.setPendingRange({ start: null, end: null });
      // Restaurar el rango original si es necesario
      actions.restoreOriginalRange();
    }
    actions.closePicker();
  };

  const getInputValue = () => {
    // Usar las fechas confirmadas para el input, no las pendientes
    if (mode === 'single') {
      return formatDateForInput(selectedDateForInput);
    } else {
      return formatRangeForInput(selectedRangeForInput);
    }
  };

  return (
    <div className={cn('ui-imj', 'relative', className)}>
      <DateInput
        ref={inputRef}
        value={getInputValue()}
        placeholder={placeholder}
        label={label}
        size={size}
        isOpen={state.isOpen}
        onInputClick={actions.openPicker}
        className={inputClassName}
        format={format}
        disabled={disabled}
      />

      <DatePickerPortal
        isOpen={!disabled && state.isOpen && isPortalReady}
        onClose={actions.closePicker}
        portalPosition={state.portalPosition}
        currentDate={state.currentDate}
        selectedDate={selectedDate}
        selectedRange={selectedRange}
        onDateSelect={handleDateSelect}
        onDateHover={handleDateHover}
        onViewModeChange={actions.setViewMode}
        onNavigate={actions.navigate}
        onMonthSelect={actions.handleMonthSelect}
        onYearSelect={actions.handleYearSelect}
        onGoToToday={actions.goToToday}
        viewMode={state.viewMode}
        minDate={minDate}
        maxDate={maxDate}
        locale={locale}
        weekDays={weekDays}
        monthNames={monthNames}
        startOfWeek={startOfWeek}
        showWeekNumbers={showWeekNumbers}
        yearRange={yearRange}
        hoverDate={state.hoverDate}
        closeOnSelect={closeOnSelect}
        autoCloseRange={autoCloseRange}
        calendarsCount={calendarsCount}
        mode={mode}
        onAccept={handleAccept}
        onCancel={handleCancel}
        renderHeader={renderHeader}
        renderDay={renderDay}
      />
    </div>
  );
};

export { DatePicker };
